import React from 'react';
import { Navigate, useOutletContext, useParams } from 'react-router-dom';
import { CORE_PAGE_KEYS, PAGE_ROUTES, type PageKey } from '../../router/pageRegistry';
import { RECORD_PAGES, heldTabs, recordPath, type RecordPageKey, type RecordTab as RecordTabEntry } from '../../router/recordPages';
import type { AuthContext } from '../Login';
import { useTenantActions } from '../../contexts/useTenantActions';

const useAllowedPages = (): ReadonlySet<PageKey> =>
  useOutletContext<AuthContext | null>()?.allowedPages ?? new Set<PageKey>(CORE_PAGE_KEYS);

/**
 * A record's bare address, a tab it does not have, or a tab the reader does
 * not hold, opens the first of its tabs the reader holds. Absolute, because a
 * path relative to a splat route resolves below the segment it did not
 * recognise.
 */
export function RecordTabLanding({ record }: { record: RecordPageKey }) {
  const allowedPages = useAllowedPages();
  const { actions } = useTenantActions();
  const params = useParams();
  const page = RECORD_PAGES[record];
  const first = heldTabs(page, allowedPages, actions)[0];
  const known = Object.fromEntries(
    Object.entries(params).filter((entry): entry is [string, string] => entry[1] !== undefined),
  );

  return <Navigate to={first ? recordPath(record, known, first.segment) : PAGE_ROUTES[page.list]} replace />;
}

/** A tab's route: its content when the reader holds the grant the record table gives it. */
export function RecordTab({
  record,
  segment,
  children,
}: {
  record: RecordPageKey;
  segment: string;
  children: React.ReactNode;
}) {
  const allowedPages = useAllowedPages();
  const { actions } = useTenantActions();
  const tab: RecordTabEntry | undefined = RECORD_PAGES[record].tabs.find((candidate) => candidate.segment === segment);
  // Until the reader's actions arrive, a tab needing one neither shows nor sends them elsewhere.
  if (tab?.action !== undefined && actions === null) return null;
  if (!tab || !heldTabs(RECORD_PAGES[record], allowedPages, actions).includes(tab)) {
    return <RecordTabLanding record={record} />;
  }
  return <>{children}</>;
}
